import { useEffect, useState } from "react"

import { Badge, Button, Dialog } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { useOutOfOfficeDialog } from "@/stores/out-of-office-store"
import {
	BackOnPicker,
	CoverPicker,
	defaultBackOn,
	memberName,
	useCovers,
} from "./out-of-office-pickers"

const threadCount = (n: number) => `${n} ${n === 1 ? "thread" : "threads"}`

export function OutOfOfficeDialog() {
	const { isOpen, setOpen } = useOutOfOfficeDialog()
	const { session } = useSession()
	const covers = useCovers(session?.userId)
	const [backOn, setBackOn] = useState(defaultBackOn)
	const [coverId, setCoverId] = useState<string | null>(null)

	useEffect(() => {
		if (isOpen) setBackOn(defaultBackOn())
	}, [isOpen])

	const cover =
		covers.find((member) => member.userId === coverId) ?? covers[0] ?? null
	const coverFirstName = memberName(cover).split(" ")[0]

	const preview = api.outOfOffice.preview.useQuery(undefined, {
		enabled: isOpen,
	})
	const utils = api.useUtils()
	const start = api.outOfOffice.start.useMutation()

	const turnOn = async () => {
		if (!cover) return
		try {
			await start.mutateAsync({ until: backOn, coverId: cover.userId })
			await utils.outOfOffice.status.invalidate()
			analytics.track("turn-on-out-of-office")
			setOpen(false)
		} catch {
			toast.error({ title: "Failed to turn on out of office" })
		}
	}

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={setOpen}
			title="Going out of office"
			footer={
				<div className="flex w-full justify-end gap-2">
					<Button type="button" variant="ghost" onClick={() => setOpen(false)}>
						Cancel
					</Button>
					<Button
						type="button"
						onClick={() => void turnOn()}
						isLoading={start.isLoading}
						disabled={!cover}
					>
						Turn on
					</Button>
				</div>
			}
		>
			<div className="mt-3 flex flex-col gap-4">
				<div className="grid grid-cols-2 gap-3">
					<div className="flex flex-col items-start gap-1.5">
						<div className="text-xs text-secondary">Back on</div>
						<BackOnPicker value={backOn} onChange={setBackOn} align="start" />
					</div>
					<div className="flex min-w-0 flex-col items-start gap-1.5">
						<div className="text-xs text-secondary">Covered by</div>
						<CoverPicker
							covers={covers}
							cover={cover}
							onChange={setCoverId}
							align="start"
						/>
					</div>
				</div>

				{cover && (
					<div className="flex flex-col divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-gray-700/80 dark:border-gray-700/80 dark:bg-gray-850">
						<div className="flex items-center justify-between gap-3 px-3 py-2.5">
							<div className="flex flex-col gap-0.5">
								<div className="text-sm font-medium">
									{threadCount(preview.data?.waitingOnUs ?? 0)} waiting on you
								</div>
								<div className="text-xs text-secondary">
									Move to {coverFirstName} now
								</div>
							</div>
							<Badge variant="green" size="sm">
								Move
							</Badge>
						</div>
						<div className="flex items-center justify-between gap-3 px-3 py-2.5">
							<div className="flex flex-col gap-0.5">
								<div className="text-sm font-medium">
									{preview.data?.waitingOnCustomer ?? 0} waiting on the customer
								</div>
								<div className="text-xs text-secondary">
									Move to {coverFirstName} when they reply
								</div>
							</div>
							<Badge variant="default" size="sm">
								On reply
							</Badge>
						</div>
					</div>
				)}
			</div>
		</Dialog>
	)
}
